import React, { useState } from 'react';
import { Clock, Users, TrendingUp, BookOpen, Award, MapPin, DollarSign, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface CourseData {
  title: string;
  shortName: string;
  duration: string;
  description: string;
  overview: string;
  eligibility: string[];
  careerProspects: {
    roles: string[];
    industries: string[];
    averageSalary: string;
    topRecruiters: string[];
  };
  curriculum: {
    year: number;
    subjects: string[];
  }[];
  admissionProcess: string[];
  topColleges: {
    name: string;
    location: string;
    fees: string;
    ranking?: string;
  }[];
  lawColleges?: {
    government: {
      slNo: number;
      name: string;
      location: string;
      type: string;
    }[];
    private: {
      slNo: number;
      name: string;
      location: string;
      type: string;
    }[];
  };
  agriculturalColleges?: {
    slNo: number;
    name: string;
    location: string;
  }[];
  hotelManagementColleges?: {
    slNo: number;
    code?: string;
    name: string;
    location: string;
  }[];
  skills: string[];
  futureScope: string;
  faqs: {
    question: string;
    answer: string;
  }[];
}

interface CourseTemplateProps {
  courseData: CourseData;
}

const CourseTemplate: React.FC<CourseTemplateProps> = ({ courseData }) => {
  const [activeTab, setActiveTab] = useState<'government' | 'private'>('government');
  const [showCollegeTables, setShowCollegeTables] = useState(false);

  // Start the animation after component mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowCollegeTables(true);
    }, 1500); // Delay before starting animation
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl font-bold mb-4">{courseData.title}</h1>
              <p className="text-xl mb-4">{courseData.shortName}</p>
              <p className="text-lg opacity-90 mb-6">{courseData.description}</p>
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  <span>{courseData.duration}</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  <span>High Demand</span>
                </div>
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  <span>Growing Field</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Quick Facts</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Duration:</span>
                  <span className="font-medium">{courseData.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span>Average Salary:</span>
                  <span className="font-medium">{courseData.careerProspects.averageSalary}</span>
                </div>
                <div className="flex justify-between">
                  <span>Job Opportunities:</span>
                  <span className="font-medium text-green-300">Excellent</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Course Overview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <BookOpen className="h-6 w-6 mr-2 text-blue-600" />
                Course Overview
              </h2>
              <p className="text-gray-700 leading-relaxed">{courseData.overview}</p>
            </div>

            {/* Curriculum */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Curriculum Structure</h2>
              <div className="space-y-6">
                {courseData.curriculum.map((year, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Year {year.year}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {year.subjects.map((subject, subIndex) => (
                        <div key={subIndex} className="flex items-center text-gray-700">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          <span>{subject}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Career Prospects */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <TrendingUp className="h-6 w-6 mr-2 text-blue-600" />
                Career Prospects
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Job Roles</h3>
                  <ul className="space-y-2">
                    {courseData.careerProspects.roles.map((role, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        {role}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Industries</h3>
                  <ul className="space-y-2">
                    {courseData.careerProspects.industries.map((industry, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="h-4 w-4 text-blue-500 mr-2" />
                        {industry}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Skills Developed */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Skills You'll Develop</h2>
              <div className="flex flex-wrap gap-3">
                {courseData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Future Scope */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Future Scope</h2>
              <p className="text-gray-700 leading-relaxed">{courseData.futureScope}</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Eligibility */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Award className="h-5 w-5 mr-2 text-blue-600" />
                Eligibility Criteria
              </h3>
              <ul className="space-y-2">
                {courseData.eligibility.map((criteria, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{criteria}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Top Recruiters */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top Recruiters</h3>
              <div className="space-y-2">
                {courseData.careerProspects.topRecruiters.map((recruiter, index) => (
                  <div key={index} className="text-gray-700 text-sm font-medium">
                    • {recruiter}
                  </div>
                ))}
              </div>
            </div>

            {/* Admission Process */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Admission Process</h3>
              <ol className="space-y-3">
                {courseData.admissionProcess.map((step, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Top Colleges */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top Colleges</h3>
              <div className="space-y-4">
                {courseData.topColleges.slice(0, 5).map((college, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900 text-sm">{college.name}</h4>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {college.location}
                    </div>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <DollarSign className="h-3 w-3 mr-1" />
                      {college.fees}
                    </div>
                    {college.ranking && (
                      <div className="text-xs text-blue-600 mt-1">Rank: {college.ranking}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {courseData.faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Law Colleges Table - Landscape Format */}
        {courseData.lawColleges && (
          <motion.div 
            className="bg-white rounded-lg shadow-sm p-8 mt-8 w-full max-w-none"
            initial={{ y: "100vh", opacity: 0 }}
            animate={{ 
              y: showCollegeTables ? 0 : "100vh", 
              opacity: showCollegeTables ? 1 : 0 
            }}
            transition={{ 
              duration: 2.5,
              ease: "easeInOut",
              type: "spring",
              stiffness: 50,
              damping: 20
            }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Law Colleges in Tamil Nadu</h2>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-6">
              <button
                onClick={() => setActiveTab('government')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'government'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Government Colleges ({courseData.lawColleges.government.length})
              </button>
              <button
                onClick={() => setActiveTab('private')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'private'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Private Colleges ({courseData.lawColleges.private.length})
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto w-full">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <table className="w-full min-w-full border-collapse border border-gray-300 table-auto">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-900 w-16">
                        Sl No
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-900 w-1/2">
                        College Name
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-900 w-1/4">
                        Location
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-900 w-1/4">
                        Type
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseData.lawColleges[activeTab].map((college, index) => (
                      <motion.tr
                        key={college.slNo}
                        initial={{ opacity: 0, x: -50, scale: 0.9 }}
                        animate={{ 
                          opacity: showCollegeTables ? 1 : 0, 
                          x: showCollegeTables ? 0 : -50,
                          scale: showCollegeTables ? 1 : 0.9
                        }}
                        transition={{ 
                          duration: 0.8, 
                          delay: (index * 0.15) + 2.5,
                          ease: "easeOut"
                        }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="border border-gray-300 px-6 py-4 text-center font-medium">
                          {college.slNo}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">
                          {college.name}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 text-gray-700">
                          {college.location}
                        </td>
                        <td className="border border-gray-300 px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            college.type === 'Government' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {college.type}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Agricultural Colleges Table - Landscape Format */}
        {courseData.agriculturalColleges && (
          <motion.div 
            className="mt-8 bg-white rounded-lg shadow-sm p-8 w-full max-w-none"
            initial={{ y: "100vh", opacity: 0 }}
            animate={{ 
              y: showCollegeTables ? 0 : "100vh", 
              opacity: showCollegeTables ? 1 : 0 
            }}
            transition={{ 
              duration: 3, 
              ease: "easeInOut",
              type: "spring",
              stiffness: 40,
              damping: 25,
              delay: 0.5
            }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Agricultural Colleges in Tamil Nadu
            </h2>
            <div className="overflow-x-auto w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200"
              >
                <table className="w-full min-w-full table-auto">
                  <thead className="bg-gradient-to-r from-green-50 to-blue-50">
                    <tr>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-20">
                        S.No
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-3/5">
                        College Name
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-1/3">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseData.agriculturalColleges.map((college, index) => (
                      <motion.tr
                        key={college.slNo}
                        initial={{ opacity: 0, x: -50, scale: 0.9 }}
                        animate={{ 
                          opacity: showCollegeTables ? 1 : 0, 
                          x: showCollegeTables ? 0 : -50,
                          scale: showCollegeTables ? 1 : 0.9
                        }}
                        transition={{ 
                          duration: 0.8, 
                          delay: (index * 0.2) + 3.5,
                          ease: "easeOut"
                        }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="border border-gray-300 px-6 py-4 text-center font-medium">
                          {college.slNo}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">
                          {college.name}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 text-gray-700">
                          {college.location}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Hotel Management Colleges Table - Landscape Format */}
        {courseData.hotelManagementColleges && (
          <motion.div 
            className="mt-8 bg-white rounded-lg shadow-sm p-8 w-full max-w-none"
            initial={{ y: "100vh", opacity: 0 }}
            animate={{ 
              y: showCollegeTables ? 0 : "100vh", 
              opacity: showCollegeTables ? 1 : 0 
            }}
            transition={{ 
              duration: 3.5, 
              ease: "easeInOut",
              type: "spring",
              stiffness: 35,
              damping: 30,
              delay: 1
            }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Tamil Nadu Hotel Management Colleges</h2>
            <div className="overflow-x-auto w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200"
              >
                <table className="w-full min-w-full table-auto">
                  <thead className="bg-gradient-to-r from-orange-50 to-red-50">
                    <tr>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-16">
                        S.No
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-20">
                        Code
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-1/2">
                        College Name
                      </th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-semibold text-gray-800 w-1/3">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseData.hotelManagementColleges.map((college, index) => (
                      <motion.tr
                        key={college.slNo}
                        initial={{ opacity: 0, x: -50, scale: 0.9 }}
                        animate={{ 
                          opacity: showCollegeTables ? 1 : 0, 
                          x: showCollegeTables ? 0 : -50,
                          scale: showCollegeTables ? 1 : 0.9
                        }}
                        transition={{ 
                          duration: 0.8, 
                          delay: (index * 0.25) + 4.0,
                          ease: "easeOut"
                        }}
                        className="hover:bg-orange-50 transition-colors"
                      >
                        <td className="border border-gray-300 px-6 py-4 text-center font-medium">
                          {college.slNo}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 text-center font-medium text-orange-600">
                          {college.code || 'N/A'}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">
                          {college.name}
                        </td>
                        <td className="border border-gray-300 px-6 py-4 text-gray-700 font-medium">
                          {college.location}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CourseTemplate;
